/**
 * /render/:id/:style/:aspect — naked render route for server-side PNG capture.
 *
 * This page exists solely for the Playwright export pipeline. It mounts a
 * single <MoodCard size="full"> at the exact target export dimensions with
 * NO chrome (no header, no nav, transparent body), waits for fonts + a
 * paint frame, then signals readiness via `window.__renderReady = true`.
 *
 * The backend `/api/cards/{id}/export.png` endpoint navigates a headless
 * Chromium to this URL, waits for the signal, then screenshots the inner
 * card element. The result is pixel-identical to the in-app preview because
 * it IS the in-app preview — same React tree, same CSS, same fonts, same
 * rendering engine.
 */
import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { MoodCard } from "../components/MoodCard";
import { VARIANTS, resolveVariant } from "../lib/cardVariants";
import { STYLE_PACKS, isStylePackV2 } from "../lib/stylePacks";

const API = `${window.location.origin}/api`;

// Aspect → pixel size table (matches the share-presets we already export).
const ASPECT_SIZES = {
    story:    { w: 1080, h: 1920 },  // 9:16 IG / TikTok story
    feed:     { w: 1080, h: 1080 },  // 1:1 IG feed
    portrait: { w: 1080, h: 1350 },  // 4:5 IG portrait
    x:        { w: 1200, h: 675  },  // 16:9 X/Twitter
};

export default function RenderCard() {
    const { id, style } = useParams();
    const [search] = useSearchParams();
    const aspect = search.get("aspect") || "story";
    const qrFlag = search.get("qr") === "1";
    const dims = ASPECT_SIZES[aspect] || ASPECT_SIZES.story;

    const [mood, setMood] = useState(null);
    const [error, setError] = useState(null);
    const wrapperRef = useRef(null);

    // Force the page background to transparent so screenshots don't carry
    // the app's dark navy outside the card. (BrandBadge / InstallBanner /
    // platform-injected badges are hidden by the Playwright pipeline via
    // page.add_style_tag — see /app/backend/png_render.py — so we don't
    // pollute production with /render-specific style hacks.)
    useEffect(() => {
        const prevBody = document.body.style.background;
        const prevHtml = document.documentElement.style.background;
        document.body.style.background = "transparent";
        document.documentElement.style.background = "transparent";
        return () => {
            document.body.style.background = prevBody;
            document.documentElement.style.background = prevHtml;
        };
    }, []);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/moodcards/${id}`);
                if (alive) setMood(data);
            } catch (e) {
                if (alive) setError(String(e?.response?.status || e.message));
            }
        })();
        return () => { alive = false; };
    }, [id]);

    // Signal readiness once: card rendered + fonts loaded + 2 paint frames flushed.
    useEffect(() => {
        if (!mood || !wrapperRef.current) return;
        let cancelled = false;
        (async () => {
            try { await document.fonts.ready; } catch { /* ignore */ }
            // Two RAFs for layout + paint settling. mix-blend-mode and gradients
            // need this — single RAF sometimes captures pre-composite frames.
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (cancelled) return;
                    // Add a tiny extra delay for any CSS animations to seed-out
                    // (they're frozen at this position once captured).
                    setTimeout(() => { window.__renderReady = true; }, 200);
                });
            });
        })();
        return () => { cancelled = true; };
    }, [mood]);

    if (error) {
        return (
            <div style={{ background: "transparent", color: "#fff", padding: 24 }}>
                error loading card: {error}
            </div>
        );
    }
    if (!mood) {
        return <div style={{ background: "transparent" }} />;
    }

    const card = mood.cards?.find((c) => c.style === style) || mood.cards?.[0];
    if (!card) {
        return (
            <div style={{ background: "transparent", color: "#fff", padding: 24 }}>
                style "{style}" not found on this card
            </div>
        );
    }

    // The screenshot target is `#render-target` at exactly the export dimensions.
    // We render the MoodCard inside it with the same `size="full"` flag the
    // in-app CardDetail page uses, so typography hierarchy matches.
    return (
        <div
            id="render-target"
            ref={wrapperRef}
            data-testid="render-target"
            style={{
                width: dims.w,
                height: dims.h,
                position: "relative",
                background: "transparent",
                overflow: "hidden",
                margin: 0,
                padding: 0,
                // CSS variable hint: aspect ratio override so MoodCard's
                // aspect-[9/16] class is honored by setting the container.
                lineHeight: 1,
            }}
        >
            {/* MoodCard internally enforces aspect-[9/16]. For 1:1 / 4:5 / 16:9
                aspects we override the wrapping aspect-ratio class with a CSS
                override that lets the inner card fill the entire render target. */}
            <style>{`
                #render-target [data-testid^="mood-card-"] {
                    aspect-ratio: unset !important;
                    width: 100% !important;
                    height: 100% !important;
                    border-radius: 0 !important;
                    border: none !important;
                    box-shadow: none !important;
                    transform: none !important;
                }
            `}</style>
            <MoodCard
                card={card}
                moodId={id}
                size="full"
                showWatermark={mood.watermark_required !== false}
                lane={mood.lane || mood.personalize?.lane || null}
                temperature={mood.personalize?.temperature || mood.temperature || null}
                qrUrl={qrFlag ? (mood.creator_qr_destination || (mood.slug ? `${window.location.origin}/remix/${mood.slug}` : null)) : null}
                variant={(() => {
                    // Legacy CSS-filter variant — applies on top of pack background.
                    // Only used when card.pack is NOT a Style Pack v2 entry; v2
                    // packs ignore the legacy variant (recipe drives everything).
                    if (isStylePackV2(mood.pack)) return null;
                    const v = search.get("variant");
                    if (v) return VARIANTS.find((x) => x.id === v) || null;
                    return resolveVariant(id);
                })()}
                recipe={(() => {
                    // Style Pack v2 — recipe override from query (?recipe=cinematic.golden-hour
                    // or ?recipe=cinematic.__custom__ for user-uploaded photos).
                    // Drawer-applied swaps live in localStorage which Playwright
                    // can't read; the export caller passes ?recipe= so the swap
                    // is honoured.
                    if (!isStylePackV2(mood.pack)) return null;
                    const key = search.get("recipe");
                    if (!key) return null; // MoodCard will resolve from mood_id deterministically
                    const [pid, vid] = key.split(".");
                    const pdef = STYLE_PACKS[pid];
                    if (!pdef) return null;
                    // Custom-bg recipe — build a synthetic variant pointing at
                    // the card's stored URL.
                    if (vid === "__custom__") {
                        const customUrl = mood.custom_background_url;
                        if (!customUrl) return null;
                        const absolute = customUrl.startsWith("/")
                            ? `${window.location.origin}${customUrl}`
                            : customUrl;
                        const base = pdef.variants[0];
                        return {
                            pack: pdef,
                            variant: {
                                ...base,
                                id: "__custom__",
                                label: "Your photo",
                                background: `linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.38) 100%), url("${absolute}") center/cover no-repeat, #1a1715`,
                                readability: "busy-bg",
                                _isCustom: true,
                            },
                            key,
                        };
                    }
                    const vdef = pdef.variants.find((x) => x.id === vid);
                    if (!vdef) return null;
                    return { pack: pdef, variant: vdef, key };
                })()}
            />
        </div>
    );
}
